/** @format */
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, input, InputSignal, ViewEncapsulation } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { StateService } from '@uirouter/angular';
import {
    OpenUniversityCart,
    StudyVoucherPurchaseActivationInformation,
    StudyVoucherPurchaseNotReadyForActivationResponse,
    StudyVoucherPurchaseReadyForActivationResponse,
    StudyVoucherPurchaseStatusResponse,
} from 'common-typescript/types';
import { Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { AppErrorHandler, OpenUniversityCartCustomerService, StudyVoucherPaymentService } from 'sis-components';

@Component({
    selector: 'app-study-voucher-payment-confirmation',
    standalone: false,
    templateUrl: './study-voucher-payment-confirmation.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyVoucherPaymentConfirmationComponent {
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly stateService = inject(StateService);
    private readonly studyVoucherPaymentService = inject(StudyVoucherPaymentService);
    private readonly openUniversityCartCustomerService = inject(OpenUniversityCartCustomerService);

    /**
     * Transaction id received from Opin.fi.
     */
    readonly transactionId: InputSignal<string> = input.required<string>();

    /**
     * Emits activation information, if the purchase is ready for activation. Otherwise (when checking the status or the purchase cannot be
     * activated), emits null.
     */
    protected readonly activationInformation$: Observable<StudyVoucherPurchaseActivationInformation | null>;

    constructor() {
        this.activationInformation$ = toObservable(this.transactionId).pipe(
            switchMap(
                transactionId =>
                    transactionId
                        ? this.studyVoucherPaymentService.getPurchaseStatus(transactionId).pipe(
                              switchMap(
                                  (
                                      response: StudyVoucherPurchaseStatusResponse,
                                  ): Observable<StudyVoucherPurchaseActivationInformation | null> => {
                                      if (isStudyVoucherPurchaseReadyForActivation(response)) {
                                          return of(response.activationInformation);
                                      }

                                      // The cart was already activated during the payment callback (an active, valid
                                      // student was resolved from the payment's personal identity code), so the purchase
                                      // is complete and the Suomi.fi activation step is not needed. Fetch the activated
                                      // cart and hand it to the completion view via cartParam, the same way the Suomi.fi
                                      // activation path does. Other reasons (UNPAID, CANCELLED) are genuine failures.
                                      if (
                                          isStudyVoucherPurchaseNotReadyForActivation(response) &&
                                          response.reason === 'COMPLETED' &&
                                          response.openUniversityCartId
                                      ) {
                                          return this.openUniversityCartCustomerService.getById(response.openUniversityCartId).pipe(
                                              map((openUniversityCart): StudyVoucherPurchaseActivationInformation | null => {
                                                  this.goToConfirmationCompleted(openUniversityCart);
                                                  // keep showing the loading screen until the redirect happens
                                                  return null;
                                              }),
                                              catchError((error: unknown) => {
                                                  // When the cart was activated for someone who was not logged in as its
                                                  // owner while paying (a guest checkout whose personal identity code
                                                  // resolved to an active, valid student), the current anonymous browser
                                                  // session is not permitted to read the cart and gets a 403. The purchase
                                                  // is already complete server-side, so this is not a failure: route to the
                                                  // completion view, which redirects to the frontpage when it has no cart to
                                                  // show. Any other error is surfaced normally.
                                                  if (error instanceof HttpErrorResponse && error.status === 403) {
                                                      this.goToConfirmationCompleted(null);
                                                      return of(null);
                                                  }
                                                  return throwError(() => error);
                                              }),
                                          );
                                      }

                                      this.stateService.go('student.open-university-cart.study-voucher-payment-failed');
                                      // keep showing the loading screen until the redirect happens
                                      return of(null);
                                  },
                              ),
                              this.appErrorHandler.defaultErrorHandler(),
                              // show loading screen during purchase status check
                              startWith(null),
                          )
                        : of(null), // show loading screen if a proper transactionId not (yet) provided
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    private goToConfirmationCompleted(openUniversityCart: OpenUniversityCart | null): void {
        this.stateService.go(
            'student.open-university-cart.study-voucher-confirmation-completed',
            { cartParam: openUniversityCart },
            { custom: { skipConfirmationDialog: true } },
        );
    }
}

function isStudyVoucherPurchaseReadyForActivation(
    response: StudyVoucherPurchaseStatusResponse,
): response is StudyVoucherPurchaseReadyForActivationResponse {
    return response.readyForActivation === 'yes';
}

function isStudyVoucherPurchaseNotReadyForActivation(
    response: StudyVoucherPurchaseStatusResponse,
): response is StudyVoucherPurchaseNotReadyForActivationResponse {
    return response.readyForActivation === 'no';
}
